import React from "react";
import { Sparkle, Lightning, Target, TrendUp, ArrowsClockwise, Lightning as Bolt } from "@phosphor-icons/react";

export default function AIInsightsPanel({ insights, loading, metrics, onRefresh, refreshing }) {
  const cached = insights?._cached;
  return (
    <div
      className="rounded-md border border-blue-200 bg-blue-50/60 p-5 relative overflow-hidden"
      data-testid="ai-insights-panel"
    >
      <div className="absolute top-0 left-0 right-0 h-[3px] ai-shimmer-bar" />
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
          <Sparkle size={20} weight="fill" className="ai-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">AI Risk Advisor</h3>
            <span className="text-[10px] uppercase tracking-wider text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">
              AI Engine
            </span>
            {cached && !loading && (
              <span
                className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1"
                title="Served from cache — invalidated automatically when observations change"
                data-testid="ai-insights-cached-badge"
              >
                <Bolt size={9} weight="fill" /> Cached
              </span>
            )}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing || loading}
                className="ml-auto p-1 rounded hover:bg-blue-100 text-blue-700 disabled:opacity-50"
                title="Re-run AI analysis"
                data-testid="ai-insights-refresh-btn"
              >
                <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
              </button>
            )}
          </div>
          {loading ? (
            <div className="space-y-2 mt-3" data-testid="ai-insights-loading">
              <div className="h-3 bg-blue-100 rounded ai-shimmer-bar w-3/4" />
              <div className="h-3 bg-blue-100 rounded ai-shimmer-bar w-1/2" />
              <div className="h-3 bg-blue-100 rounded ai-shimmer-bar w-2/3" />
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                {insights?.headline || "No observations yet. Add observations to generate insights."}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <div className="bg-white rounded-md border border-blue-100 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    <TrendUp size={12} /> Residual Risk Score
                  </div>
                  <div className="text-2xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "Geist" }}>
                    {metrics?.residual_risk_score ?? 0}
                    <span className="text-sm text-slate-400">/100</span>
                  </div>
                  <div className="text-[11px] text-emerald-600 font-medium mt-0.5">
                    {metrics?.reduction_pct ?? 0}% reduction from resolutions
                  </div>
                </div>
                <div className="bg-white rounded-md border border-blue-100 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    <Target size={12} /> Top Themes
                  </div>
                  <ul className="text-xs text-slate-700 mt-1 space-y-0.5">
                    {(insights?.top_themes || []).slice(0, 3).map((t, i) => (
                      <li key={`th-${i}-${t}`} className="truncate">• {t}</li>
                    ))}
                    {!insights?.top_themes?.length && <li className="text-slate-400">—</li>}
                  </ul>
                </div>
                <div className="bg-white rounded-md border border-blue-100 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    <Lightning size={12} /> Quick Wins
                  </div>
                  <ul className="text-xs text-slate-700 mt-1 space-y-0.5">
                    {(insights?.quick_wins || []).slice(0, 3).map((t, i) => (
                      <li key={`qw-${i}-${t}`} className="truncate">• {t}</li>
                    ))}
                    {!insights?.quick_wins?.length && <li className="text-slate-400">—</li>}
                  </ul>
                </div>
              </div>

              {!!insights?.recommendations?.length && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Recommendations
                  </div>
                  <ul className="text-sm text-slate-700 space-y-1.5">
                    {insights.recommendations.map((r, i) => (
                      <li key={`rec-${i}-${r}`} className="flex gap-2">
                        <span className="text-blue-500">→</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
